<template>
  <Heading
    v-if="!hideTitle"
    :level="2"
    :title="$t('product.show.basicDetails.detailsToRemember')"
    h-class="text-h4 font-medium"
    class="kft-heading--left mt-12 mb-2"
  />

  <div
    class="accordion accordion-icon-toggle kft-accordion"
    itemscope
    itemtype="https://schema.org/FAQPage"
  >
    <details
      v-for="(ok, i) in computedEventData"
      :key="i"
      :data-id="ok.type"
      class="accordion-item"
      :open="defaultOpen"
      itemprop="mainEntity"
      itemscope
      itemtype="https://schema.org/Question"
    >
      <summary class="accordion-header">
        <component :is="ok.icon" class="icon" width="24" height="24" />
        <h3 class="text-lg font-normal leading-6 ml-2" itemprop="name">
          {{ ok.title }}
        </h3>
        <IconChevronDown class="chevron" />
      </summary>
      <div
        class="accordion-body"
        itemprop="acceptedAnswer"
        itemscope
        itemtype="https://schema.org/Answer"
      >
        <div itemprop="text" v-html="ok.description"></div>
      </div>
    </details>
  </div>
</template>

<script setup lang="ts">
import IconTable from '~/assets/icons/event/note-stroke.svg?component'
import IconPeople from '~/assets/icons/event/event-summary-people.svg?component'
import IconDuration from '~/assets/icons/event/event-info-duration.svg?component'
import IconLocation from '~/assets/icons/event/event-info-location.svg?component'
import IconLang from '~/assets/icons/event/event-info-lang.svg?component'
import IconIncluded from '~/assets/icons/event/event-summary-pencil.svg?component'
import IconChevronDown from '~/assets/icons/chevron-down.svg?component'
import { useTitleByCategory } from '~/composables/product/useGetTitleByCategory'
import type { EventDescription } from '~/composables/types/event'

const { t: $t } = useI18n()
const { getTitleByCategory } = useTitleByCategory()

const {
  eventData = {} as EventDescription,
  isGiftcard = false,
  hideTitle = false,
  defaultOpen = false,
} = defineProps<{
  eventData: EventDescription
  isGiftcard: boolean
  hideTitle: boolean
  defaultOpen: boolean
}>()

const getLocationDescription: ComputedRef<string | null> = computed(() => {
  let text: string | null = null

  if (eventGetters.getLocationSummary(eventData)) {
    text = eventGetters.getLocationSummary(eventData)
  } else {
    text = eventGetters.getHasMultipleLocations(eventData)
      ? $t('product.show.giftcardIcons.multipleLocationsSummary')
      : $t('product.show.giftcardIcons.locationContent')
  }

  if (eventGetters.getHasMobileClasses(eventData)) {
    text += `<br> ${$t('product.show.basicDetails.mobileClassLocation')}`
  }

  return text
})

const computedEventData = computed(() => {
  return [
    {
      type: 'bring',
      icon: IconTable,
      title: getTitleByCategory('whatToBring', eventData, isGiftcard),
      description: eventGetters.getWhatToBring(eventData),
    },
    {
      type: 'included',
      icon: IconIncluded,
      title: getTitleByCategory('whatIsIncluded', eventData, isGiftcard),
      description: eventGetters.getWhatIsIncluded(eventData),
    },
    {
      type: 'people',
      icon: IconPeople,
      title: getTitleByCategory('teamEventAndPrivate', eventData, isGiftcard),
      description:
        eventGetters.getTeamEventsAndGiftCardsSummary(eventData) ||
        $t('product.show.giftcardIcons.teamEventsAndGiftCardsContent'),
    },
    {
      type: 'duration',
      icon: IconDuration,
      title: getTitleByCategory('duration', eventData, isGiftcard),
      description: isGiftcard
        ? $t('product.show.giftcardIcons.durationContent')
        : convertMinutesToHumanString(
            +eventGetters.getNextDateAvailableDuration(eventData) ||
              +eventGetters.getDefaultDuration(eventData) ||
              +eventGetters.getEventMinimumDuration(eventData) ||
              +eventGetters.getEventMaximumDuration(eventData)
          ),
    },
    {
      type: 'location',
      icon: IconLocation,
      title: $t('product.show.icons.locationSummary'),
      description: getLocationDescription.value,
    },
    {
      type: 'language',
      icon: IconLang,
      title: getTitleByCategory('languages', eventData, isGiftcard),
      description: isGiftcard
        ? $t(`product.show.giftcardIcons.languages.content`)
        : eventGetters
            .getLanguages(eventData)
            ?.map((i) => $t(`product.show.icons.languages.list.${i}`))
            .join(', '),
    },
  ]
})
</script>

<style lang="postcss">
.accordion {
  border-radius: 10px;
  @apply border border-stone-200;
}

.accordion-item {
  @apply border-b border-stone-200 pb-5;

  &[open] {
    .chevron {
      transform: rotate(180deg);
    }
    .accordion-header {
      @apply pb-1.5;
    }
  }
}

.accordion-header {
  @apply relative select-none flex cursor-pointer py-5 pb-0 pl-12 pr-9;

  .icon {
    @apply absolute top-5 left-4;
  }

  .chevron {
    @apply absolute top-6 right-4 duration-300 ease-in-out;
  }

  &::-webkit-details-marker {
    display: none;
  }
}

.accordion-body {
  @apply pt-0 px-12 pl-14;
  @apply text-stone-700;
}
</style>
